import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, API_BASE } from "@/lib/apiClient";
import {
  confirmAnfrageMailCandidate,
  listAnfrageCandidateAttachments,
  type AnfrageMailCandidate,
  type AnfrageMailCandidateTarget,
} from "@/lib/anfragen";
import { fmtMailDate, matchReasonLabel } from "@/pages/anfragen/anfrageWizardUtils";

interface AttachmentRow {
  id: string;
  name?: string;
  contentType?: string;
  size?: number;
}

function fmtAttachmentSize(bytes?: number): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  return `${Math.round(n / 1024)} KB`;
}

export function ConfirmMailCandidateDialog({
  open,
  anfrageId,
  candidate,
  targets,
  onClose,
  onConfirmed,
}: {
  open: boolean;
  anfrageId: number | null;
  candidate: AnfrageMailCandidate | null;
  targets: AnfrageMailCandidateTarget[];
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const [selectedTargets, setSelectedTargets] = useState<Set<number>>(new Set());
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(new Set());
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [includeEml, setIncludeEml] = useState(true);
  const [moveInbox, setMoveInbox] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !candidate || !anfrageId) return;
    setIncludeEml(true);
    setMoveInbox(true);
    setError(null);

    const defaultTargets = new Set<number>();
    for (const t of targets) {
      if (t.sharepoint_folder_id) defaultTargets.add(t.auftrag_id);
    }
    setSelectedTargets(defaultTargets);

    const summary = (candidate.attachments_summary || [])
      .filter((a) => a?.id != null)
      .map((a) => ({
        id: String(a.id),
        name: a.name ?? undefined,
        contentType: a.contentType ?? undefined,
        size: a.size ?? undefined,
      }));
    setAttachments(summary);
    setSelectedAttachments(new Set(summary.map((a) => a.id)));

    if (!candidate.graph_message_id) return;

    setAttachmentsLoading(true);
    void listAnfrageCandidateAttachments(anfrageId, candidate.id)
      .then((rows) => {
        if (!rows.length) return;
        const mapped = rows
          .filter((a) => a?.id != null)
          .map((a) => ({
            id: String(a.id),
            name: a.name,
            contentType: a.contentType,
            size: a.size,
          }));
        setAttachments(mapped);
        setSelectedAttachments(new Set(mapped.map((a) => a.id)));
      })
      .catch(() => {
        /* keep summary fallback */
      })
      .finally(() => setAttachmentsLoading(false));
  }, [open, anfrageId, candidate, targets]);

  async function submit() {
    if (!anfrageId || !candidate) return;
    const sharepoint_targets = [...selectedTargets].map((auftrag_id) => ({ auftrag_id }));
    if (!sharepoint_targets.length) {
      setError("Mindestens einen Auftragsordner auswählen.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await confirmAnfrageMailCandidate(anfrageId, candidate.id, {
        sharepoint_targets,
        include_eml: includeEml,
        selected_attachment_ids: [...selectedAttachments],
        move_in_mailbox_to: moveInbox ? "Verarbeitete Anfragen" : null,
      });
      onConfirmed();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bestätigen fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  function previewAttachment(attId: string) {
    if (!anfrageId || !candidate) return;
    const url = `${API_BASE}/anfragen/${anfrageId}/mail-candidates/${candidate.id}/attachments/${encodeURIComponent(attId)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const taPreview =
    (candidate?.matched_ta_nummern || [])[0] || (anfrageId ? `Anfrage-${anfrageId}` : "{TA}");
  const matchHint = (candidate?.match_reasons || []).map(matchReasonLabel).join(", ");

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Antwort-Mail bestätigen"
      size="lg"
      zIndexClass="z-[120]"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void submit()} loading={loading}>
            Bestätigen &amp; ablegen
          </Button>
        </>
      }
    >
      {candidate ? (
        <div className="mb-4 space-y-1 text-sm">
          <p>
            <strong>Von:</strong>{" "}
            {[candidate.from_name, candidate.from_email ? `<${candidate.from_email}>` : ""]
              .filter(Boolean)
              .join(" ") || "–"}
          </p>
          <p>
            <strong>Betreff:</strong> {candidate.subject || "(ohne Betreff)"}
          </p>
          <p>
            <strong>Empfangen:</strong> {fmtMailDate(candidate.received_at)}
          </p>
          {matchHint ? <p className="text-label-secondary">Match: {matchHint}</p> : null}
        </div>
      ) : null}

      {error ? <p className="mb-3 text-sm text-status-red">{error}</p> : null}

      <fieldset className="mb-4 rounded-ios border border-separator p-3">
        <legend className="px-1 text-sm font-semibold text-label">SharePoint-Ablage</legend>
        <p className="mb-2 text-xs text-label-secondary">
          Mail (als <code className="text-label">.eml</code>) und ausgewählte Anhänge werden in die markierten
          Auftragsordner gelegt.
        </p>
        <div className="space-y-1">
          {targets.map((t) => {
            const disabled = !t.sharepoint_folder_id;
            const checked = selectedTargets.has(t.auftrag_id);
            return (
              <label
                key={t.auftrag_id}
                className={`flex cursor-pointer gap-2 rounded-md px-2 py-1 text-sm ${disabled ? "opacity-50" : "hover:bg-fill-tertiary"}`}
                title={disabled ? "Kein SharePoint-Ordner für diesen Auftrag" : undefined}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                  disabled={disabled}
                  checked={checked}
                  onChange={(e) => {
                    setSelectedTargets((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(t.auftrag_id);
                      else next.delete(t.auftrag_id);
                      return next;
                    });
                  }}
                />
                <span>
                  <strong>{t.auftragsnummer || `Auftrag ${t.auftrag_id}`}</strong>
                  {t.beschreibung ? ` – ${t.beschreibung}` : ""}
                  {(t.ta_nummern || []).length ? (
                    <span className="ml-1 rounded-full border border-separator bg-fill-tertiary px-1.5 py-0.5 text-xs">
                      {(t.ta_nummern || []).join(", ")}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-label-tertiary">
          Pfad innerhalb des Auftragsordners: <code>03 - Lieferanten/Anfragen/{taPreview}</code>
        </p>
      </fieldset>

      <fieldset className="mb-4 rounded-ios border border-separator p-3">
        <legend className="px-1 text-sm font-semibold text-label">Anhänge</legend>
        {attachmentsLoading ? (
          <p className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Anhänge werden geladen …
          </p>
        ) : attachments.length === 0 ? (
          <p className="text-xs text-label-secondary">Keine Dateianhänge für diese Nachricht.</p>
        ) : (
          <ul className="space-y-1">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-separator px-2 py-1"
              >
                <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                    checked={selectedAttachments.has(a.id)}
                    onChange={(e) => {
                      setSelectedAttachments((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(a.id);
                        else next.delete(a.id);
                        return next;
                      });
                    }}
                  />
                  <span className="min-w-0 break-all">
                    {a.name || "(unbenannt)"}{" "}
                    <span className="text-label-tertiary">
                      {[a.contentType, fmtAttachmentSize(a.size)].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </label>
                <Button type="button" variant="secondary" size="sm" onClick={() => previewAttachment(a.id)}>
                  Vorschau
                </Button>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      <label className="mb-2 flex cursor-pointer items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
          checked={includeEml}
          onChange={(e) => setIncludeEml(e.target.checked)}
        />
        <span>Mail-Datei (.eml) mit ablegen</span>
      </label>

      <label className="flex cursor-pointer items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
          checked={moveInbox}
          onChange={(e) => setMoveInbox(e.target.checked)}
        />
        <span>Mail im Postfach in Ordner „Verarbeitete Anfragen“ verschieben</span>
      </label>
    </Dialog>
  );
}
